import { Button } from "@/components-shared/ui/button";
import {
  APIProvider,
  Map as GoogleMap,
  MapCameraChangedEvent,
} from "@vis.gl/react-google-maps";
import React, { useCallback, useEffect, useState } from "react";
import { ClusteredPropMarkers } from "./ClusteredPropMarker";
import { useGetPropsMap } from "../utils/get-props-hook";
import { cn } from "@/lib/utils";
import { useTranslations } from "next-intl";
import { useUpdateSearchParam } from "../utils/useUpdateSearchParam";

const RESTRICTION_BOUNDS = {
  north: 32.1543,
  south: 16.5,
  west: 34.5,
  east: 55.6667,
};

const Map = ({
  bounds,
  setBounds,
  setMapValues,
  handleClickOnMarker,
}: {
  bounds: google.maps.LatLngBoundsLiteral;
  setBounds: React.Dispatch<
    React.SetStateAction<google.maps.LatLngBoundsLiteral>
  >;
  setMapValues: React.Dispatch<
    React.SetStateAction<google.maps.LatLngBoundsLiteral | undefined>
  >;
  handleClickOnMarker: (prop: Property) => void;
}) => {
  const updateSearchParam = useUpdateSearchParam();

  const [isBoundsChanged, setIsBoundsChanged] = React.useState<boolean>(false);
  const t = useTranslations();
  const [zoom, setZoom] = useState(11);
  const {
    data: propsForMap,
    isLoading: isMapLoading,
    isFetching: isMapFetching,
    setMapValues: setMapValuesMap,
  } = useGetPropsMap(100000, bounds);

  const handleSearch = () => {
    setMapValues(bounds);
    setMapValuesMap(bounds);
    setIsBoundsChanged(false);
    updateSearchParam("page", "1");
  };

  const handleBoundsChanged = useCallback((event: MapCameraChangedEvent) => {
    setBounds(event.detail.bounds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setIsBoundsChanged(true);
  }, [bounds]);

  return (
    <div className="sm:sticky sm:top-24 transition-all max-w-2xl w-full h-[400px] rounded-2xl overflow-hidden">
      {
        <Button
          className={cn("absolute top-2 left-2 z-10", {
            hidden: !isBoundsChanged && !isMapLoading && !isMapFetching,
          })}
          disabled={!isBoundsChanged || isMapLoading || isMapFetching}
          onClick={handleSearch}
        >
          {isMapLoading || isMapFetching ? (
            <>
              <span className="loading loading-spinner loading-sm me-3" />
              <span>{t("map.searching")}</span>
            </>
          ) : (
            t("map.search")
          )}
        </Button>
      }

      <APIProvider
        apiKey={process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY as string}
      >
        <GoogleMap
          defaultCenter={{ lat: 21.56734486931291, lng: 39.17262644909372 }}
          restriction={{
            latLngBounds: RESTRICTION_BOUNDS,
            strictBounds: true,
          }}
          zoom={zoom}
          onZoomChanged={(e) => setZoom(e.detail.zoom)}
          mapId={process.env.NEXT_PUBLIC_GOOGLE_MAPS_ID}
          onBoundsChanged={handleBoundsChanged}
          onCameraChanged={handleBoundsChanged}
        >
          {propsForMap?.properties && (
            <ClusteredPropMarkers
              onClick={handleClickOnMarker}
              props={propsForMap.properties}
            />
          )}
        </GoogleMap>
      </APIProvider>
    </div>
  );
};

export default Map;
